import type { AttributeColumn, MetricColumn } from '@/types/Discover';
import { METRICS_DATABASE_NAME } from '@/utils/metrics';
import { createDate, convertToBusiness } from '@/utils/dateUtils';
import { PeriodAbbrevEnum } from './PeriodAbbrevEnum';
import { QuantSpaceUniverseTypeConstants } from './QuantSpaceUniverseTypeConstants';
import { ScatterChartColorLogic } from './ScatterChartColorLogic';
import type { DateTime } from 'luxon';
import { PinnedItemsChartTypes } from './PinnedItems';

/**
 * Supported Time Periods for Data Discovery
 */
export enum QuantSpacePeriod {
  TEN_Y = PeriodAbbrevEnum.TEN_Y,
  FIVE_Y = PeriodAbbrevEnum.FIVE_Y,
  THREE_Y = PeriodAbbrevEnum.THREE_Y,
}
export enum DiscoverPeriod {
  TEN_Y = PeriodAbbrevEnum.TEN_Y,
  FIVE_Y = PeriodAbbrevEnum.FIVE_Y,
  THREE_Y = PeriodAbbrevEnum.THREE_Y,
  ONE_Y = PeriodAbbrevEnum.ONE_Y,
  SIX_M = PeriodAbbrevEnum.SIX_M,
  ONE_M = PeriodAbbrevEnum.ONE_M,
  YTD = PeriodAbbrevEnum.YTD,
}

const prevBusinessDay = convertToBusiness(createDate().minus({ days: 1 }));
export const DiscoveryPeriodEndDate = prevBusinessDay;
export const DiscoveryPeriodStartDateMap: Record<DiscoverPeriod, DateTime> = {
  [PeriodAbbrevEnum.TEN_Y]: convertToBusiness(prevBusinessDay.minus({ years: 10 })),
  [PeriodAbbrevEnum.FIVE_Y]: convertToBusiness(prevBusinessDay.minus({ years: 5 })),
  [PeriodAbbrevEnum.THREE_Y]: convertToBusiness(prevBusinessDay.minus({ years: 3 })),
  [PeriodAbbrevEnum.ONE_Y]: convertToBusiness(prevBusinessDay.minus({ years: 1 })),
  [PeriodAbbrevEnum.SIX_M]: convertToBusiness(prevBusinessDay.minus({ months: 6 })),
  [PeriodAbbrevEnum.ONE_M]: convertToBusiness(prevBusinessDay.minus({ months: 1 })),
  [PeriodAbbrevEnum.YTD]: convertToBusiness(prevBusinessDay.startOf('year').minus({ days: 1 })),
};

export const SortedDiscoveryPeriods: readonly DiscoverPeriod[] = Object.entries(DiscoveryPeriodStartDateMap)
  .sort((a, b) => (a[1] < b[1] ? -1 : 1))
  .map((o) => o[0] as DiscoverPeriod);

/** Returns periods in longest-to-shortest coverage order (subset of SortedDiscoveryPeriods). */
export function sortDiscoverPeriodsLongestFirst(periods: readonly DiscoverPeriod[]): DiscoverPeriod[] {
  const periodSet = new Set(periods);

  return SortedDiscoveryPeriods.filter((period) => periodSet.has(period));
}

/** Longest available period whose window covers history from startDate onward. */
export function findLongestPeriodForHistoryStart(
  periods: readonly DiscoverPeriod[],
  historyStartDate: DateTime,
): DiscoverPeriod | undefined {
  return sortDiscoverPeriodsLongestFirst(periods).find(
    (period) => DiscoveryPeriodStartDateMap[period] >= historyStartDate,
  );
}

export const QuantSpacePeriods: readonly QuantSpacePeriod[] = [
  QuantSpacePeriod.THREE_Y,
  QuantSpacePeriod.FIVE_Y,
  QuantSpacePeriod.TEN_Y,
];

export const QuantSpaceDefaultPeriodByUniverse: Record<QuantSpaceUniverseTypeConstants, QuantSpacePeriod> = {
  [QuantSpaceUniverseTypeConstants.QIS]: QuantSpacePeriod.FIVE_Y,
  [QuantSpaceUniverseTypeConstants.FUND]: QuantSpacePeriod.TEN_Y,
  [QuantSpaceUniverseTypeConstants.HEDGE_FUND]: QuantSpacePeriod.TEN_Y,
};

export const QuantSpaceDefaultColorByUniverse: Record<QuantSpaceUniverseTypeConstants, ScatterChartColorLogic> = {
  [QuantSpaceUniverseTypeConstants.QIS]: ScatterChartColorLogic.ASSET_CLASS,
  [QuantSpaceUniverseTypeConstants.FUND]: ScatterChartColorLogic.FUND_CATEGORY,
  [QuantSpaceUniverseTypeConstants.HEDGE_FUND]: ScatterChartColorLogic.FUND_CATEGORY,
};

export const isMetricColumn = (column: MetricColumn | AttributeColumn): column is MetricColumn => {
  return (column as MetricColumn).timePeriod !== undefined;
};

/**
 * NOTE: this variable affects the defaults set in `useUniverseTableColumnPreferences`.
 * If you modify this, you should invalidate LocalStorageEntities.UNIVERSE_TABLE_COLUMN_REFERENCES
 */
export const DEFAULT_DISCOVER_PERIOD = DiscoverPeriod.ONE_Y as const;

export const LEGEND_SWATCH_SIZE = 9;

export enum FilterType {
  Inclusive = 'Inclusive',
  Exclusive = 'Exclusive',
}

export enum MetricColumnFilterTypes {
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_OR_EQUAL_TO = 'LESS_THAN_OR_EQUAL_TO',
  EQUALS = 'EQUALS',
  GREATER_THAN_OR_EQUAL_TO = 'GREATER_THAN_OR_EQUAL_TO',
  GREATER_THAN = 'GREATER_THAN',
}

export enum StaticColumnName {
  NAME = 'NAME',
  LIVE_DATE = 'LIVE_DATE',
}

/** Metrics offered in the Data Discover column dropdown metric selector. */
export type DiscoverColumnMetricSelectorOption = METRICS_DATABASE_NAME | StaticColumnName.LIVE_DATE;

export const DISCOVER_COLUMN_METRIC_SELECTOR_OPTIONS: DiscoverColumnMetricSelectorOption[] = [
  METRICS_DATABASE_NAME.Return,
  METRICS_DATABASE_NAME.ReturnCumulative,
  METRICS_DATABASE_NAME.Volatility,
  METRICS_DATABASE_NAME.Sharpe,
  METRICS_DATABASE_NAME.InfoRatio,
  METRICS_DATABASE_NAME.Skew,
  METRICS_DATABASE_NAME.Kurtosis,
  METRICS_DATABASE_NAME.JarqueBera,
  METRICS_DATABASE_NAME.Calmar,
  METRICS_DATABASE_NAME.Omega,
  METRICS_DATABASE_NAME.Sortino,
  METRICS_DATABASE_NAME.Var,
  METRICS_DATABASE_NAME.Var99,
  METRICS_DATABASE_NAME.Mvar,
  METRICS_DATABASE_NAME.Mvar99,
  METRICS_DATABASE_NAME.Cvar,
  METRICS_DATABASE_NAME.Cvar99,
  METRICS_DATABASE_NAME.AvgDD,
  METRICS_DATABASE_NAME.MaxDD,
  METRICS_DATABASE_NAME.PercentPosWeeks,
  METRICS_DATABASE_NAME.PercentPosMonths,
  StaticColumnName.LIVE_DATE,
];

export enum AttributeColumnFilterTypes {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
}

/** Chart types offered in the Data Discover sidebar cart compare view. */
export const DISCOVER_PINNED_CHART_TYPE_OPTIONS: readonly PinnedItemsChartTypes[] =
  Object.values(PinnedItemsChartTypes);
